'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { X } from 'lucide-react'

import { cn } from '../utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = ({ children, ...props }) => (
  <DialogPrimitive.Portal
    container={document.getElementById('portal-root') ?? undefined}
    {...props}
  >
    {children}
  </DialogPrimitive.Portal>
)

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-[1070] bg-black/40 data-[state=closed]:pointer-events-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(
  ({ className, dialogCloseClass, xClass, children, ...props }, ref) => {
    const localRef = React.useRef(null)
    const composedRef = useComposedRefs(ref, localRef)

    React.useEffect(() => {
      if (!localRef.current) return

      // Inert shell regions so axe (and AT) can't reach content behind the overlay.
      // Depth counter lets nested dialogs stack safely.
      const shellIds = ['top_bar', 'main_content']
      const shellEls = shellIds
        .map((id) => document.getElementById(id))
        .filter(Boolean)

      shellEls.forEach((el) => {
        const depth = parseInt(el.dataset.dialogInertDepth || '0', 10)
        el.dataset.dialogInertDepth = depth + 1
        if (depth === 0) el.setAttribute('inert', '')
      })

      // Run cleanup immediately when data-state=closed — don't wait for animationend.
      let cleaned = false
      const doCleanup = () => {
        if (cleaned) return
        cleaned = true
        shellEls.forEach((el) => {
          const depth = parseInt(el.dataset.dialogInertDepth || '1', 10) - 1
          if (depth <= 0) {
            el.removeAttribute('inert')
            delete el.dataset.dialogInertDepth
          } else {
            el.dataset.dialogInertDepth = depth
          }
        })
      }

      const contentEl = localRef.current
      const observer = new MutationObserver(() => {
        if (contentEl.getAttribute('data-state') === 'closed') {
          observer.disconnect()
          doCleanup()
        }
      })
      observer.observe(contentEl, {
        attributes: true,
        attributeFilter: ['data-state'],
      })

      return () => {
        observer.disconnect()
        doCleanup()
      }
    }, [])

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={composedRef}
          className={cn(
            // CTS-9775: rounded-2xl + overflow-hidden so all four corners stay rounded
            // (header uses rounded-t; without overflow-hidden the body can look square at the bottom)
            'fixed left-[50%] top-[75px] z-[1080] flex max-h-[calc(100vh-4rem)] w-full max-w-[94vw] -translate-x-[50%] flex-col gap-4 overflow-hidden rounded-2xl bg-background duration-1000 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] md:max-w-lg',
            className,
          )}
          {...props}
        >
          {children}
          {/* CTS-9206, CTS-9267: close control in accessibility tree; decorative icon hidden */}
          <DialogPrimitive.Close
            aria-label="Close"
            className={cn(
              'absolute right-[14px] top-[14px] rounded-full text-white opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
              dialogCloseClass,
            )}
          >
            <X
              aria-hidden="true"
              className={
                props['variant'] == 'noHeader'
                  ? ''
                  : xClass
                    ? xClass
                    : 'text-white'
              }
            />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  },
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn(
      'ml-[-1px] mt-[-1px] flex w-[calc(100%+2px)] items-center gap-3 rounded-t-2xl bg-primary px-6 py-4 text-white',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'flex flex-col-reverse px-4 py-0 sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      '!text-xl text-lg font-normal leading-none tracking-tight',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('px-4 text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
